import type React from "react"

import { useAuth } from "./auth-context"
import { useNavigate, useLocation } from "react-router"
import { useEffect, useRef } from "react"
import type { Role } from "@/lib/team/roles"
import { useCanAccess, useCurrentRole } from "@/lib/team/use-current-role"
import { useTeamMembers } from "@/lib/api/use-teams"
import { useOrganization } from "@/lib/organization/organization-context"

interface ProtectedRouteProps {
  children: React.ReactNode
  /**
   * Roles that may access this route. Title-Case wire values from
   * `Roles` in `@/lib/team/roles`. Super-admins (`user.admin === true`)
   * bypass this list.
   */
  allowedRoles?: readonly Role[]
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const canAccess = useCanAccess(allowedRoles ?? [])
  const currentRole = useCurrentRole()
  // Track whether the role lookup has resolved (members list loaded). If
  // there's no allowedRoles to check, treat as "ready" immediately. We avoid
  // a redirect-flicker on first paint while the role is loading.
  const {
    activeGroup,
    activeDealership,
    activeBodyShop,
    activeTeamKind,
    activeTeamId,
    isGroupView,
    isLoading: orgLoading,
  } = useOrganization()
  const teamId =
    isGroupView && activeGroup
      ? Number(activeGroup.id)
      : activeTeamKind === 'dealership' && activeDealership
      ? Number(activeDealership.id)
      : activeTeamKind === 'bodyshop' && activeBodyShop
      ? Number(activeBodyShop.id)
      : null
  const { data: members, isLoading: rolesLoading } = useTeamMembers(teamId)
  // `roleResolved` only counts as resolved once the org-context has finished
  // hydrating AND, if we have a teamId, the members query has settled. Without
  // the orgLoading gate, a group user (Director/GM) sees `teamId === null` for
  // a brief window after auth flips ready and the route bounces them straight
  // to /unauthorized before activeDealership lands from the second-effect
  // groupDealerships fetch.
  const roleResolved =
    !allowedRoles ||
    user?.admin ||
    (!orgLoading && (!teamId || !rolesLoading))

  // De-dupe denial logs across re-renders. The effect deps flip multiple times
  // as auth/org/members resolve, but the denial *decision* should print once.
  const loggedDenialRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isLoading && !user) {
      const key = `unauth:${location.pathname}`
      if (loggedDenialRef.current !== key) {
        loggedDenialRef.current = key
        // eslint-disable-next-line no-console
        console.groupCollapsed(
          `%c[ProtectedRoute] redirect → /login (no authenticated user)`,
          'color:#dc2626;font-weight:600',
        )
        // eslint-disable-next-line no-console
        console.log('reason:', 'auth.user is null after auth-context finished loading')
        // eslint-disable-next-line no-console
        console.log('path:', location.pathname)
        // eslint-disable-next-line no-console
        console.log('allowedRoles:', allowedRoles ?? '(none — auth-only gate)')
        // eslint-disable-next-line no-console
        console.groupEnd()
      }
      navigate("/login")
    }
    if (!isLoading && user && allowedRoles && roleResolved && !canAccess) {
      const key = `denied:${location.pathname}:${currentRole ?? 'null'}`
      if (loggedDenialRef.current !== key) {
        loggedDenialRef.current = key
        // eslint-disable-next-line no-console
        console.groupCollapsed(
          `%c[ProtectedRoute] redirect → /unauthorized (role check failed)`,
          'color:#dc2626;font-weight:600',
        )
        // eslint-disable-next-line no-console
        console.log('reason:', user.admin
          ? 'super-admin bypass should have passed but canAccess=false (likely a hook bug)'
          : currentRole === null
          ? 'user has no TeamUser row on the active team and no inherited Director→GM access'
          : `current role "${currentRole}" not in allowedRoles`)
        // eslint-disable-next-line no-console
        console.log('path:', location.pathname)
        // eslint-disable-next-line no-console
        console.log('allowedRoles:', allowedRoles)
        // eslint-disable-next-line no-console
        console.log('currentRole:', currentRole)
        // eslint-disable-next-line no-console
        console.log('user:', {
          id: user.id,
          email: user.email,
          admin: user.admin,
        })
        // eslint-disable-next-line no-console
        console.log('activeTeam:', {
          kind: isGroupView ? 'group' : activeTeamKind,
          id: isGroupView ? activeGroup?.id ?? null : activeTeamId,
          group: activeGroup ? { id: activeGroup.id, name: activeGroup.name } : null,
          dealership: activeDealership ? { id: activeDealership.id, name: activeDealership.name } : null,
          bodyshop: activeBodyShop ? { id: activeBodyShop.id, name: activeBodyShop.name } : null,
        })
        // eslint-disable-next-line no-console
        console.log(
          'members:',
          members?.map(m => ({ id: m.id, email: m.email, role: m.role })) ?? '(none — query returned no rows)',
        )
        // eslint-disable-next-line no-console
        console.groupEnd()
      }
      navigate("/unauthorized")
    }
  }, [
    user,
    isLoading,
    navigate,
    allowedRoles,
    canAccess,
    roleResolved,
    currentRole,
    members,
    activeGroup,
    activeDealership,
    activeBodyShop,
    activeTeamKind,
    activeTeamId,
    isGroupView,
    location.pathname,
  ])

  // Show the spinner while either auth or the role check is still resolving.
  // Without the `!roleResolved` arm, the page would briefly render its
  // children before the unauthorized redirect fires — a flash that looks
  // like a broken access check.
  if (isLoading || (!!allowedRoles && user && !roleResolved)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-900 border-t-transparent mx-auto mb-4" />
          <p className="text-zinc-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  if (allowedRoles && roleResolved && !canAccess) {
    return null
  }

  return <>{children}</>
}
